import { useState, useEffect, useRef } from "react";

const usePiBrowserDetection = () => {
  const [isPiBrowser, setIsPiBrowser] = useState(null);
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectionMethod, setDetectionMethod] = useState(null);
  const timeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    let cancelled = false;

    const detectEnvironment = async () => {
      const detectWithTimeout = (promise, timeoutMs = 5000) => {
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve("timeout"), timeoutMs)
        );
        return Promise.race([promise, timeoutPromise]);
      };

      try {
        // Layer 1: Check if Pi SDK exists
        if (typeof window !== 'undefined' && window.Pi) {
          //console.log("🔍 Pi object detected, checking methods...");
          
          // Deep inspection of the Pi object
          const piObject = window.Pi;
          //const piKeys = Object.keys(piObject || {});
          //const piOwnProps = Object.getOwnPropertyNames(piObject || {});
          //const piDescriptors = Object.getOwnPropertyDescriptors(piObject || {});
          
          //console.log("🔍 Comprehensive Pi object inspection:", {
            //PiObject: piObject,
            //PiKeys: piKeys,
            //PiOwnProps: piOwnProps,
            //PiDescriptors: piDescriptors,
            //PiProto: Object.getPrototypeOf(piObject || {}),
          //});

          // Enhanced flag detection - check multiple locations
          let isProd = false;
          let isSandbox = false;
          
          // Method 1: Check all possible locations for Prod/Sandbox flags
          const checkLocations = [
            piObject, // Direct object
            Object.getPrototypeOf(piObject), // Prototype
            piObject.constructor?.prototype, // Constructor prototype
            window.Pi, // Ensure we're checking the right reference
          ].filter(Boolean);

          for (const location of checkLocations) {
            try {
              // Check for Prod flag
              if ('Prod' in location) {
                const prodValue = location.Prod;
                //console.log(`🔍 Found Prod in location:`, location.constructor?.name || 'Unknown', prodValue);
                if (prodValue === true || prodValue === 'true') {
                  isProd = true;
                }
              }
              
              // Check for Sandbox flag
              if ('Sandbox' in location) {
                const sandboxValue = location.Sandbox;
                //console.log(`🔍 Found Sandbox in location:`, location.constructor?.name || 'Unknown', sandboxValue);
                if (sandboxValue === true || sandboxValue === 'true') {
                  isSandbox = true;
                }
              }

              // Check with property descriptors
              const prodDescriptor = Object.getOwnPropertyDescriptor(location, 'Prod');
              const sandboxDescriptor = Object.getOwnPropertyDescriptor(location, 'Sandbox');
              
              if (prodDescriptor) {
                //console.log("🔍 Prod descriptor found:", prodDescriptor);
                const prodValue = prodDescriptor.get ? prodDescriptor.get.call(piObject) : prodDescriptor.value;
                if (prodValue === true || prodValue === 'true') {
                  isProd = true;
                }
              }
              
              if (sandboxDescriptor) {
                //console.log("🔍 Sandbox descriptor found:", sandboxDescriptor);
                const sandboxValue = sandboxDescriptor.get ? sandboxDescriptor.get.call(piObject) : sandboxDescriptor.value;
                if (sandboxValue === true || sandboxValue === 'true') {
                  isSandbox = true;
                }
              }
            } catch (e) {
              //console.log("🔍 Error checking location:", e);
            }
          }

          // Method 2: Try to iterate through ALL properties including non-enumerable ones
          try {
            let currentObj = piObject;
            while (currentObj && currentObj !== Object.prototype) {
              const allProps = Object.getOwnPropertyNames(currentObj);
              //console.log(`🔍 Checking properties in ${currentObj.constructor?.name || 'Unknown'}:`, allProps);
              
              for (const prop of allProps) {
                if (prop === 'Prod' || prop === 'Sandbox') {
                  try {
                    const value = currentObj[prop];
                    //console.log(`🔍 Found ${prop} via iteration:`, value);
                    if (prop === 'Prod' && (value === true || value === 'true')) {
                      isProd = true;
                    }
                    if (prop === 'Sandbox' && (value === true || value === 'true')) {
                      isSandbox = true;
                    }
                  } catch (e) {
                    //console.log(`🔍 Error accessing ${prop}:`, e);
                  }
                }
              }
              currentObj = Object.getPrototypeOf(currentObj);
            }
          } catch (e) {
            //console.log("🔍 Error in property iteration:", e);
          }

          //console.log("🔍 Final flag determination:", { isProd, isSandbox });
          
          if (isProd || isSandbox) {
            //console.log("🏁 Pi environment flags detected - this is definitely Pi Browser", { isProd, isSandbox });
            if (!cancelled) {
              setIsPiBrowser(true);
              setDetectionMethod(isProd ? "prodFlag" : "sandboxFlag");
              return;
            }
          }
          
          // Method 3: The most reliable check - getPiHostAppInfo behavior
          if (typeof window.Pi.getPiHostAppInfo === 'function') {
            try {
              const result = await detectWithTimeout(window.Pi.getPiHostAppInfo(), 3000);
              //console.log("📱 getPiHostAppInfo result:", result);
              
              if (!cancelled && result !== "timeout") {
                // Standard Pi Browser response
                if (typeof result === 'object' && result.hostApp === "pi-browser") {
                  //console.log("🏁 Pi Browser production environment detected");
                  setIsPiBrowser(true);
                  setDetectionMethod("getPiHostAppInfo");
                  return;
                }
                
                // Pi Sandbox behavior: ONLY empty object indicates real Pi Sandbox
                if (typeof result === 'object' && Object.keys(result).length === 0) {
                  //console.log("🏁 Pi Sandbox detected: getPiHostAppInfo returned empty object (authentic Pi environment)");
                  setIsPiBrowser(true);
                  setDetectionMethod("piSandboxBehavior");
                  return;
                }
                
                // Any other non-timeout result from a real Pi environment
                if (result !== null && result !== undefined) {
                  //console.log("🏁 Pi Browser detected: getPiHostAppInfo returned valid response");
                  setIsPiBrowser(true);
                  setDetectionMethod("getPiHostAppInfoSuccess");
                  return;
                }
              } else if (result === "timeout") {
                //console.log("⚠️ getPiHostAppInfo timed out - likely mock Pi SDK in regular browser");
                // Continue to other checks, but this is a strong indicator of mock SDK
              }
            } catch (error) {
              console.warn("⚠️ getPiHostAppInfo failed:", error);
              //console.log("⚠️ getPiHostAppInfo failure indicates mock Pi SDK in regular browser");
              // Continue to other checks, but this failure indicates mock SDK
            }
          }

          // Method 4: Check for Pi-specific error patterns
          // Real Pi Browser will show specific postMessage errors when not properly configured
          const hasPostMessageErrors = window.console && 
            window.console.error && 
            typeof window.console.error === 'function';
          
          if (hasPostMessageErrors) {
            // Check if there are any Pi-related error messages in the console
            // Note: This is a heuristic and may not always work
            //console.log("🔍 Checking for Pi-specific error patterns...");
          }

          // Method 5: User Agent check (more reliable than method presence)
          const ua = navigator?.userAgent?.toLowerCase() || "";
          //console.log("🔍 User Agent:", ua);
          
          const piUserAgentPatterns = [
            'pibrowser',
            'pi browser',
            'pi-browser',
          ];

          if (piUserAgentPatterns.some(pattern => ua.includes(pattern))) {
            console.log("🌐 Pi Browser detected via User Agent");
            if (!cancelled) {
              setIsPiBrowser(true);
              setDetectionMethod("userAgent");
              return;
            }
          }

          // Method 6: Final fallback - only if we haven't detected Pi Browser yet
          // This should NOT trigger if getPiHostAppInfo worked properly above
          const piMethods = ['authenticate', 'createPayment', 'getPiHostAppInfo'];
          const availableMethods = piMethods.filter(method => typeof window.Pi[method] === 'function');
          
          //console.log("🔍 Available Pi methods:", availableMethods);
          //console.log("🔍 Reached fallback method detection - getPiHostAppInfo likely failed/timed out");
          
          // If we reach here with Pi methods, it's likely a mock SDK in a regular browser
          if (availableMethods.length >= 3) {
            //console.log("⚠️ Pi methods present but getPiHostAppInfo didn't work - likely mock Pi SDK");
            //console.log("🌐 Treating as regular browser with mock Pi SDK");
            // Don't return true here - let it fall through to default detection
          }

          // Retry logic for timing issues
          if (retryCountRef.current < maxRetries) {
            //console.log(`⏳ Pi object found but inconclusive - will retry (${retryCountRef.current + 1}/${maxRetries})`);
            retryCountRef.current++;
            timeoutRef.current = setTimeout(() => {
              if (!cancelled) {
                detectEnvironment();
              }
            }, 500 * retryCountRef.current); // Increasing delay
            return;
          }
          
          //console.log("⚠️ Pi object found but no clear indicators after retries - likely mock SDK");
        }

        // Final determination
        //console.log("🌐 No clear Pi Browser indicators found - assuming standard browser");
        if (!cancelled) {
          setIsPiBrowser(false);
          setDetectionMethod("default");
        }

      } catch (error) {
        console.error("❌ Pi Browser detection error:", error);
        
        // Final fallback
        if (!cancelled) {
          setIsPiBrowser(false);
          setDetectionMethod("error");
        }
      } finally {
        if (!cancelled) {
          setIsDetecting(false);
        }
      }
    };

    // Add a small delay to ensure DOM and scripts are loaded
    const initialDelay = setTimeout(() => {
      if (!cancelled) {
        detectEnvironment();
      }
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Debug helper
  const getDetectionInfo = () => ({
    isPiBrowser,
    isDetecting,
    detectionMethod,
    retryCount: retryCountRef.current,
    windowPiExists: typeof window !== 'undefined' && !!window.Pi,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    isLocalhost: typeof window !== 'undefined' && 
                (window.location.hostname === 'localhost' || 
                 window.location.hostname === '127.0.0.1' ||
                 window.location.hostname.includes('local'))
  });

  return { 
    isPiBrowser, 
    isDetecting, 
    detectionMethod,
    getDetectionInfo 
  };
};

export default usePiBrowserDetection;
